import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { Box, Typography, IconButton as MuiIconButton } from '@mui/material';
import { Loading } from '@gpstrackit/gtc-react-controls';

const SEVERITY_COLORS = {
    critical: '#E74C3C',
    high: '#E67E22',
    medium: '#F39C12',
    low: '#27AE60'
};

/**
 * SeverityInfoBubble — Reusable floating popover that shows
 * Critical / High / Medium / Low severity counts for a given event.
 *
 * Props:
 *  - open        {boolean}   Whether the bubble is visible
 *  - eventName   {string}    Name of the event to display as title
 *  - position    {{ x, y }}  Screen coordinates (clientX / clientY)
 *  - data        {{ critical, high, medium, low }}  Severity counts
 *  - loading     {boolean}   Show a spinner while data is being fetched
 *  - onClose     {function}  Called when the user dismisses the bubble
 */
const SeverityInfoBubble = ({
    open = false,
    eventName = '',
    position = { x: 0, y: 0 },
    data = { critical: 0, high: 0, medium: 0, low: 0 },
    loading = false,
    onClose
}) => {
    const { messages } = useIntl();
    const bubbleRef = useRef(null);

    // Close on click outside (delayed to avoid closing from the same click that opened it)
    useEffect(() => {
        if (!open) return;

        let active = false;
        const timer = setTimeout(() => { active = true; }, 100);

        const handler = (e) => {
            if (!active) return;
            if (bubbleRef.current && !bubbleRef.current.contains(e.target)) {
                onClose?.();
            }
        };

        document.addEventListener('click', handler, true);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('click', handler, true);
        };
    }, [open, onClose]);

    if (!open || !eventName) return null;

    const levels = [
        { key: 'critical', label: messages['driver_performance_severity_critical'] || 'Critical' },
        { key: 'high',     label: messages['driver_performance_severity_high']     || 'High' },
        { key: 'medium',   label: messages['driver_performance_severity_medium']   || 'Medium' },
        { key: 'low',      label: messages['driver_performance_severity_low']      || 'Low' }
    ];

    const displayName = messages[`driver_performance_${eventName?.replace(/\s+/g, '')}`] || eventName;

    // Keep bubble within viewport bounds
    const bubbleWidth = 240;
    const bubbleHeight = 200;
    const margin = 10;
    const maxLeft = window.innerWidth - bubbleWidth - margin;
    const maxTop = window.innerHeight - bubbleHeight - margin;
    const left = Math.min(position.x + margin, maxLeft);
    const top = Math.min(position.y + margin, maxTop);

    return ReactDOM.createPortal(
        <Box
            ref={bubbleRef}
            sx={{
                position: 'fixed',
                left,
                top,
                bgcolor: 'background.paper',
                borderRadius: 1,
                boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
                p: 2,
                minWidth: 220,
                zIndex: 10000
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, pb: 1, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{displayName}</Typography>
                <MuiIconButton size="small" onClick={onClose} sx={{ p: 0, ml: 1 }}>
                    <Typography sx={{ fontSize: '1.25rem', lineHeight: 1 }}>×</Typography>
                </MuiIconButton>
            </Box>

            {/* Body */}
            {loading ? (
                <Box display="flex" justifyContent="center" py={1}>
                    <Loading show />
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {levels.map(({ key, label }) => (
                        <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 0.5,
                                bgcolor: SEVERITY_COLORS[key],
                                color: 'white',
                                fontSize: '0.75rem',
                                fontWeight: 600
                            }}>
                                {label}
                            </Box>
                            <Typography sx={{ fontWeight: 600 }}>{data?.[key] ?? 0}</Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>,
        document.body
    );
};

export default SeverityInfoBubble;
